import axios from 'axios'
import { ElMessage, ElNotification } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { HTTP_STATUS } from './api'
import { authUtil } from './auth'

// Create axios instance
const getBaseURL = () => {
  const isDebug = import.meta.env.VITE_IS_DEBUG === 'true'
  if (isDebug) {
    return import.meta.env.VITE_API_DEBUG_URL
  }
  return import.meta.env.VITE_API_BASE_URL
}

const request = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor
request.interceptors.request.use(
  (config) => {
    const token = authUtil.getToken()
    const savedLang = localStorage.getItem('yolal-language') || 'zh'
    
    const normalizedMethod = (config.method || 'GET').toUpperCase()
    const isFormData = config.data instanceof FormData
    
    config.headers = {
      'Content-Type': isFormData 
        ? undefined 
        : (normalizedMethod === 'GET' ? 'application/x-www-form-urlencoded' : 'application/json'),
      'Authorization': token ? `Bearer ${token}` : '',
      'Y-Language': savedLang
    }
    
    if (normalizedMethod === 'GET') {
      if (!config.url.includes('SwiperImages') && !config.url.includes('YolalImage')) {
        config.headers['Cache-Control'] = 'no-cache, max-age=0, must-revalidate, no-store'
      }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
request.interceptors.response.use(
  (response) => {
    const { data } = response
    
    // Handle non-JSON responses
    if (!data || (typeof data === 'string' && !data.trim())) {
      return { success: true, data: response.data }
    }
    
    let actualResponse = data
    
    // Unwrap nested responses
    if (data.success === true && data.data && typeof data.data.success === 'boolean') {
      actualResponse = data.data
    } else if (data.success === true && data.data && typeof data.data.isSuccess === 'boolean') {
      actualResponse = data.data
    }
    
    // Check for subscription expired
    const msgOrErrors = actualResponse.message || (Array.isArray(actualResponse.errors) ? actualResponse.errors.join(' ') : '')
    const isPaymentExpired = actualResponse && (
      actualResponse.errorCode === 'SOFTWARE_PAYMENT_EXPIRED' ||
      (actualResponse.errors && actualResponse.errors.some(e => String(e).includes('SOFTWARE_PAYMENT_EXPIRED'))) ||
      msgOrErrors.includes('订阅已过期')
    )
    
    if (isPaymentExpired) {
      handleSoftwareLocked(actualResponse.errorMessage || actualResponse.message || '软件订阅已过期')
      return Promise.reject(new Error(actualResponse.errorMessage || actualResponse.message))
    }
    
    // Check success field
    let successValue = undefined
    if (actualResponse && typeof actualResponse.success === 'boolean') {
      successValue = actualResponse.success
    } else if (actualResponse && typeof actualResponse.Success === 'boolean') {
      successValue = actualResponse.Success
    } else if (actualResponse && typeof actualResponse.isSuccess === 'boolean') {
      successValue = actualResponse.isSuccess
    }
    
    if (actualResponse && typeof successValue === 'boolean') {
      if (successValue === true) {
        return actualResponse
      }
      
      const errorMsg = actualResponse.message || actualResponse.Message || 
        (Array.isArray(actualResponse.errors) && actualResponse.errors.length > 0 
          ? actualResponse.errors.join(', ') 
          : 'Request failed')
      
      if (isTokenExpiredError(errorMsg, Array.isArray(actualResponse.errors) ? actualResponse.errors : [])) {
        handleAuthError()
        return Promise.reject(new Error('Session expired'))
      }
      
      if (errorMsg.includes('订阅已过期') || errorMsg.includes('SOFTWARE_PAYMENT_EXPIRED')) {
        handleSoftwareLocked(errorMsg)
        return Promise.reject(new Error(errorMsg))
      }
      
      return Promise.reject(new Error(errorMsg))
    }
    
    if (response.status === HTTP_STATUS.SUCCESS) {
      return actualResponse
    }
    
    return actualResponse
  },
  (error) => {
    const { response } = error
    const userStore = useUserStore()
    
    if (response) {
      // Check for subscription expired in error responses
      const errorData = response.data
      if (errorData && typeof errorData === 'object') {
        const hasExpiredCode = errorData.errorCode === 'SOFTWARE_PAYMENT_EXPIRED' ||
          (errorData.errors && errorData.errors.some(e => String(e).includes('SOFTWARE_PAYMENT_EXPIRED'))) ||
          String(errorData.message || '').includes('订阅已过期') ||
          (errorData.errors && errorData.errors.some(e => String(e).includes('订阅已过期')))
        
        if (hasExpiredCode) {
          handleSoftwareLocked(errorData.errorMessage || errorData.message || '软件订阅已过期')
          return Promise.reject(new Error(errorData.errorMessage || errorData.message))
        }
      }
      
      switch (response.status) {
        case HTTP_STATUS.UNAUTHORIZED:
          ElNotification({
            title: 'Session Expired',
            message: 'Your session has expired. Please login again.',
            type: 'warning',
            duration: 3000
          })
          userStore.logout()
          window.location.href = '/login'
          break
          
        case HTTP_STATUS.FORBIDDEN:
          handleSoftwareLocked('软件订阅已过期')
          break
          
        case HTTP_STATUS.NOT_FOUND:
          ElMessage.error('Resource not found')
          break
          
        case HTTP_STATUS.SERVER_ERROR:
          ElMessage.error('Server error')
          break
          
        default:
          let errorMsg = 'Request failed'
          try {
            if (response.data && typeof response.data === 'object') {
              errorMsg = response.data?.message || error.message || 'Request failed'
              
              if (isTokenExpiredError(errorMsg, Array.isArray(response.data.errors) ? response.data.errors : [])) {
                handleAuthError()
                return Promise.reject(new Error('Session expired'))
              }
            } else if (typeof response.data === 'string' && response.data.trim()) {
              errorMsg = response.data?.message || error.message || 'Request failed'
            } else {
              errorMsg = error.message || 'Request failed'
            }
          } catch (e) {
            errorMsg = error.message || 'Request failed'
          }
          
          if (isTokenExpiredError(errorMsg, [])) {
            handleAuthError()
          } else {
            ElMessage.error(errorMsg)
          }
      }
    } else {
      if (error.message.includes('timeout')) {
        ElMessage.error('Request timeout')
      } else if (error.message.includes('Network Error')) {
        ElMessage.error('Network error')
      } else {
        ElMessage.error(error.message || 'Request failed')
      }
    }
    
    return Promise.reject(error)
  }
)

function isTokenExpiredError(message, errors = []) {
  const errMsgUpper = String(message || '').toUpperCase()
  const errorsArray = Array.isArray(errors) ? errors : []
  const errorsUpper = errorsArray.map(e => String(e).toUpperCase()).join(' ')
  
  const tokenErrorKeywords = [
    'TOKEN_EXPIRED', 
    'TOKEN_INVALID', 
    'UNAUTHORIZED', 
    'AUTHENTICATION REQUIRED',
    'SESSION EXPIRED'
  ]
  
  return tokenErrorKeywords.some(keyword => 
    errMsgUpper.includes(keyword) || errorsUpper.includes(keyword)
  )
}

function handleAuthError() {
  const userStore = useUserStore()
  
  userStore.clearSession()
  localStorage.removeItem('yolal-token')
  localStorage.removeItem('yolal-user')
  sessionStorage.clear()
  
  if (window.router) {
    window.router.replace('/login').catch(() => {})
  } else {
    window.location.href = '/login'
  }
}

function handleSoftwareLocked(errorMessage) {
  localStorage.setItem('software-locked', 'true')
  
  if (window.router) {
    window.router.replace('/locked').catch(() => {
      window.location.href = '/locked'
    })
  } else {
    window.location.href = '/locked'
  }
}

export default request